import { getAuthBackend } from '../../helpers/authApi'
import supabase from '../../helpers/supabaseClient'

// Constants for localStorage keys
const AUTH_USER_KEY = 'authUser'
const CURRENT_USER_KEY = 'currentUser'
const REFRESH_TIMESTAMP_KEY = 'auth_refresh_timestamp'

export const state = {
    authUser: localStorage.getItem(AUTH_USER_KEY) ? JSON.parse(localStorage.getItem(AUTH_USER_KEY)) : null,
    currentUser: localStorage.getItem(CURRENT_USER_KEY) ? JSON.parse(localStorage.getItem(CURRENT_USER_KEY)) : null,
    // Track if refresh is in progress to prevent multiple refreshes
    refreshInProgress: false,
    // Store the last refresh timestamp
    lastRefreshTimestamp: localStorage.getItem(REFRESH_TIMESTAMP_KEY) ? parseInt(localStorage.getItem(REFRESH_TIMESTAMP_KEY)) : 0,
}

export const mutations = {
    SET_AUTH_USER(state, session) {
        state.authUser = session
        if (session) {
            localStorage.setItem(AUTH_USER_KEY, JSON.stringify(session))
        } else {
            localStorage.removeItem(AUTH_USER_KEY)
        }
    },
    SET_CURRENT_USER(state, userData) {
        state.currentUser = userData
        if (userData) {
            localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(userData))
        } else {
            localStorage.removeItem(CURRENT_USER_KEY)
        }
    },
    SET_REFRESH_IN_PROGRESS(state, status) {
        state.refreshInProgress = status
    },
    SET_REFRESH_TIMESTAMP(state, timestamp) {
        state.lastRefreshTimestamp = timestamp
        localStorage.setItem(REFRESH_TIMESTAMP_KEY, timestamp.toString())
    },
    // This mutation doesn't touch localStorage - used for sync between tabs.
    // Nulls DO propagate: when another tab logs out (keys removed), this tab
    // must drop its in-memory copy too — otherwise it can later replay a
    // revoked refresh token, which revokes the entire token family.
    SYNC_AUTH_STATE(state, { authUser, currentUser, timestamp }) {
        state.authUser = authUser
        state.currentUser = currentUser
        state.lastRefreshTimestamp = timestamp || 0
    }
}

export const getters = {
    // Whether the user is currently logged in.
    loggedIn(state) {
        return !!state.authUser?.access_token
    },
    // Whether the user has completed onboarding
    hasCompletedOnboarding(state) {
        return !!state.currentUser?.completed_onboarding
    },
    // Get the access token
    accessToken(state) {
        return state.authUser?.access_token
    },
    // Get the current user profile
    user(state) {
        return state.currentUser
    }
}

export const actions = {
    // This is automatically run in `src/state/store.js` when the app
    // starts, along with any other actions named `init` in other modules.
    init({ dispatch, commit }) {
        // Add storage event listener to sync auth state across tabs
        window.addEventListener('storage', (event) => {
            if (event.key === AUTH_USER_KEY || event.key === CURRENT_USER_KEY || event.key === REFRESH_TIMESTAMP_KEY) {
                dispatch('syncFromLocalStorage')
            }
        })
        
        // Listen for Supabase auth changes
        supabase.auth.onAuthStateChange(async (event, session) => {
            if (session) {
                console.log("supabase.auth.onAuthStateChange::Event", event);
                // Signed in with Supabase
                if (event === 'SIGNED_IN') {
                    // Convert Supabase session to our app format
                    const sessionData = {
                        access_token: session.access_token,
                        refresh_token: session.refresh_token,
                        expires_at: session.expires_at
                    }
                    
                    commit('SET_AUTH_USER', sessionData)
                    commit('SET_REFRESH_TIMESTAMP', Date.now())
                    
                    // Instead of creating a profile directly from Supabase data,
                    // fetch the complete profile from our backend API
                    try {
                        const profile = await getAuthBackend().getProfile()
                        commit('SET_CURRENT_USER', profile)
                    } catch (error) {
                        console.error('Failed to fetch user profile:', error)
                        // Fallback to creating a basic profile from Supabase data
                        const basicProfile = {
                            id: session.user.id,
                            email: session.user.email,
                            first_name: session.user.user_metadata?.first_name || '',
                            last_name: session.user.user_metadata?.last_name || '',
                            completed_onboarding: true // Default to true to prevent redirect loops
                        }
                        commit('SET_CURRENT_USER', basicProfile)
                    }
                }
            }
            // SIGNED_OUT is intentionally ignored: the auth store owns the
            // session lifecycle (logOut action / refresh-failure handling).
            // supabase-js only brokers the OAuth handshake — its internal
            // sign-out events must not clear a session that is kept alive
            // via the backend /auth/refresh.
        })
        
        dispatch('validate')
    },

    // Synchronize state from localStorage (used when another tab updates the auth state)
    syncFromLocalStorage({ commit }) {
        const authUser = localStorage.getItem(AUTH_USER_KEY) 
            ? JSON.parse(localStorage.getItem(AUTH_USER_KEY)) 
            : null
        
        const currentUser = localStorage.getItem(CURRENT_USER_KEY)
            ? JSON.parse(localStorage.getItem(CURRENT_USER_KEY))
            : null
            
        const timestamp = localStorage.getItem(REFRESH_TIMESTAMP_KEY)
            ? parseInt(localStorage.getItem(REFRESH_TIMESTAMP_KEY))
            : 0
            
        commit('SYNC_AUTH_STATE', { authUser, currentUser, timestamp })
        
        return { authUser, currentUser, timestamp }
    },

    // Logs in the current user.
    logIn({ commit, dispatch, getters }, { email, password } = {}) {
        if (getters.loggedIn) return dispatch('validate')

        return getAuthBackend().loginUser(email, password).then((response) => {
            commit('SET_AUTH_USER', response.session)
            commit('SET_CURRENT_USER', response.profile)
            commit('SET_REFRESH_TIMESTAMP', Date.now())
            
            return true
        }).catch((error) => {
            throw error
        })
    },

    // Login with access token (used for email confirmation)
    loginWithToken({ commit, dispatch, getters }, { access_token } = {}) {
        if (getters.loggedIn) return dispatch('validate')

        return getAuthBackend().loginWithToken(access_token).then((response) => {
            commit('SET_AUTH_USER', response.session)
            commit('SET_CURRENT_USER', response.profile)
            commit('SET_REFRESH_TIMESTAMP', Date.now())

            return response.user
        })
    },

    // Logs out the current user.
    logOut({ commit }) {
        return new Promise((resolve, reject) => {
            getAuthBackend().logout().then(() => {
                commit('SET_AUTH_USER', null)
                commit('SET_CURRENT_USER', null)
                commit('SET_REFRESH_TIMESTAMP', 0)
                // dispatch('localStorage/clearLocalStorage', null, { root: true })
                resolve(true)
            }).catch((error) => {
                reject(error)
            })
        })
    },

    // register the user
    register({ commit, dispatch, getters }, { firstName, lastName, email, password, phone, planId, referralCode, promoCode } = {}) {
        if (getters.loggedIn) return dispatch('validate')

        return getAuthBackend().registerUser(firstName, lastName, email, password, phone, planId, referralCode, promoCode).then((response) => {
            // Ensure we have both session and profile data
            if (response && response.session && response.profile) {
                commit('SET_AUTH_USER', response.session);
                commit('SET_CURRENT_USER', response.profile);
                commit('SET_REFRESH_TIMESTAMP', Date.now());
                
                // Log the successful auth state for debugging
                console.log('Registration successful, auth state set');
            } else {
                console.warn('Registration response missing session or profile data:', response);
            }
            return response;
        });
    },

    // register the user
    // eslint-disable-next-line no-unused-vars
    resetPassword({ dispatch, getters }, { email } = {}) {
        if (getters.loggedIn) return dispatch('validate')

        return getAuthBackend().forgetPassword(email).then((response) => {
            const message = response.data
            return message
        });
    },

    // Confirm password reset with token and new password
    // eslint-disable-next-line no-unused-vars
    confirmPasswordReset({ dispatch, getters }, { token, password } = {}) {
        if (getters.loggedIn) return dispatch('validate')

        return getAuthBackend().confirmPasswordReset(token, password).then((response) => {
            return response
        });
    },

    // Validates the current user's token and refreshes it
    validate({ commit, state, dispatch }) {
        if (!state.authUser) return Promise.resolve(null);
        
        // First check if we have a valid authUser with access token
        if (!state.authUser.access_token) {
            commit('SET_AUTH_USER', null);
            return Promise.resolve(null);
        }
        
        // Check if another tab recently refreshed the token (within last 60 seconds)
        const now = Date.now()
        if (now - state.lastRefreshTimestamp < 60000) {
            return dispatch('syncFromLocalStorage')
                .then(({ currentUser }) => currentUser)
        }
        
        // Check if token refresh is already in progress
        if (state.refreshInProgress) {
            // Wait for the refresh to complete
            return new Promise(resolve => {
                const checkInterval = setInterval(() => {
                    if (!state.refreshInProgress) {
                        clearInterval(checkInterval);
                        resolve(dispatch('syncFromLocalStorage').then(({ currentUser }) => currentUser));
                    }
                }, 100);
                
                // Timeout after 5 seconds to prevent infinite waiting
                setTimeout(() => {
                    clearInterval(checkInterval);
                    // If we time out waiting for refresh, clear the flag
                    commit('SET_REFRESH_IN_PROGRESS', false);
                    resolve(dispatch('syncFromLocalStorage').then(({ currentUser }) => currentUser));
                }, 5000);
            });
        }
        
        // Check if the auth backend is available
        const authBackend = getAuthBackend();
        if (!authBackend) {
            console.warn('Auth backend not initialized yet');
            return Promise.resolve(state.currentUser);
        }
        
        // Get authenticated user data - this will automatically handle token refresh via interceptors
        return authBackend.getProfile()
            .then(profile => {
                // Update user profile if successful
                commit('SET_CURRENT_USER', profile);
                return profile;
            })
            .catch(error => {
                // If profile fetch fails, check if it's because profile doesn't exist
                console.error('Error validating user:', error);

                // Check if it's a 404 error (profile doesn't exist - OAuth user that never completed registration)
                if (error.response && error.response.status === 404) {
                    // Try to create the profile automatically using handleGoogleCallback
                    // This handles the case where a user authenticated with OAuth but profile was never created
                    console.log('Profile not found (404) - attempting to create profile for OAuth user');
                    return dispatch('handleGoogleCallback')
                        .then(response => {
                            // Profile created successfully
                            return response.profile;
                        })
                        .catch(createError => {
                            console.error('Failed to auto-create profile during validation:', createError);
                            // If we can't create the profile, clear auth state
                            commit('SET_AUTH_USER', null);
                            commit('SET_CURRENT_USER', null);
                            commit('SET_REFRESH_TIMESTAMP', 0);
                            return null;
                        });
                }

                // Check if it's a 401 error (token expired/invalid)
                if (error.response && error.response.status === 401) {
                    // Clear auth state - the axios interceptor will handle token refresh
                    commit('SET_AUTH_USER', null);
                    commit('SET_CURRENT_USER', null);
                    commit('SET_REFRESH_TIMESTAMP', 0);
                    // Ensure refreshInProgress is cleared if a 401 is caught here
                    commit('SET_REFRESH_IN_PROGRESS', false);
                }

                return null;
            });
    },

    // Update user profile during onboarding
    // eslint-disable-next-line no-unused-vars
    updateProfile({ commit }, profileData) {
        return getAuthBackend().updateProfile(profileData).then((response) => {
            commit('SET_CURRENT_USER', response)
            return response
        })
    },

    // Update user password
    updatePassword({ commit }, passwordData) {
        return getAuthBackend().updatePassword(passwordData).then((response) => {
            commit('SET_AUTH_USER', response.session)
            commit('SET_REFRESH_TIMESTAMP', Date.now())
            return response
        })
    },


    // Fetch current user profile
    // eslint-disable-next-line no-unused-vars
    fetchProfile({ commit }) {
        return getAuthBackend().getProfile().then((response) => {
            commit('SET_CURRENT_USER', response)
            return response
        })
    },

    // Get Google OAuth URL
    getGoogleAuthUrl({ commit }, { redirectTo } = {}) {
        console.log("Getting Google authentication URL", redirectTo);
        return getAuthBackend().getGoogleAuthUrl(redirectTo)
    },

    // Handle Google OAuth callback 
    // This is called after Supabase redirects back to our app
    handleGoogleCallback({ commit }) {
        return getAuthBackend().handleGoogleCallback()
            .then((response) => {
                console.log("handleGoogleCallback::Response", response);
                
                // Check if we have both session and profile
                if (response.session && response.profile) {
                    commit('SET_AUTH_USER', response.session);
                    commit('SET_CURRENT_USER', response.profile);
                    commit('SET_REFRESH_TIMESTAMP', Date.now());
                } else {
                    console.error("handleGoogleCallback::Invalid response format", response);
                    throw new Error("Invalid authentication response");
                }
                
                return response;
            });
    },

    // Login with hash token
    loginWithHash({ commit, dispatch, getters }, { hash } = {}) {
        if (getters.loggedIn) return dispatch('validate')

        return getAuthBackend().verifyLoginHash(hash).then((response) => {
            commit('SET_AUTH_USER', response.session)
            commit('SET_CURRENT_USER', response.profile)
            commit('SET_REFRESH_TIMESTAMP', Date.now())
            return response
        })
    },

    // Complete onboarding
    completeOnboarding({ commit }) {
        return getAuthBackend().updateProfile({ completed_onboarding: true })
            .then((response) => {
                commit('SET_CURRENT_USER', response)
                return response

            })
    },

    // Reset onboarding state (useful for testing)
    resetOnboarding({ commit }) {
        return getAuthBackend().updateProfile({ completed_onboarding: false })
            .then((response) => {
                commit('SET_AUTH_USER', response.session)
                commit('SET_CURRENT_USER', response)
                commit('SET_REFRESH_TIMESTAMP', Date.now())
                return response
            })
    }
}

// ===
// Private helpers
// ===
